"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAccountsStore } from "@/lib/accounts/store";
import {
  getStorageInventory,
  type StorageInventory,
} from "./inventory";
import {
  accountsFingerprint,
  invalidateStorageInventorySnapshot,
  loadStorageInventorySnapshot,
  saveStorageInventorySnapshot,
  snapshotScannedAt,
} from "./inventory-snapshot";

export function useStorageInventory() {
  const accounts = useAccountsStore((s) => s.accounts);
  const loadAccounts = useAccountsStore((s) => s.loadAccounts);
  const accountFp = useMemo(() => accountsFingerprint(accounts), [accounts]);

  const [inventory, setInventory] = useState<StorageInventory | null>(() =>
    loadStorageInventorySnapshot(accounts),
  );
  const [loading, setLoading] = useState(() => inventory === null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastScannedAt, setLastScannedAt] = useState<number | null>(() =>
    snapshotScannedAt(accounts),
  );
  const scanGeneration = useRef(0);

  const refresh = useCallback(
    async (options?: { background?: boolean }) => {
      const background = options?.background === true;
      const gen = ++scanGeneration.current;

      if (background) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const data = await getStorageInventory(accounts);
        if (gen !== scanGeneration.current) return;

        setInventory(data);
        saveStorageInventorySnapshot(accounts, data);
        setLastScannedAt(Date.now());
      } catch (e) {
        if (gen !== scanGeneration.current) return;
        setError(e instanceof Error ? e.message : "Failed to load storage");
      } finally {
        if (gen === scanGeneration.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [accounts],
  );

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    const cached = loadStorageInventorySnapshot(accounts);
    if (cached) {
      setInventory(cached);
      setLastScannedAt(snapshotScannedAt(accounts));
      setLoading(false);
      void refresh({ background: true });
      return;
    }
    void refresh({ background: false });
  }, [accountFp, refresh]);

  const invalidateAndRefresh = useCallback(async () => {
    invalidateStorageInventorySnapshot();
    scanGeneration.current += 1;
    await refresh({ background: false });
  }, [refresh]);

  return {
    inventory,
    loading,
    refreshing,
    error,
    lastScannedAt,
    refresh,
    invalidateAndRefresh,
  };
}
